import { useQuery } from '@tanstack/react-query';
import {
  patientsApi,
  type ClinicTagResponse,
  type PaginatedResponse,
  type PatientDetailResponse,
  type PatientDetailCorrectionResponse,
  type PatientListItemResponse,
  type SearchPatientsParams,
} from '../infrastructure/patients.api';

/**
 * Query keys for the patients feature. Commands invalidate against these so
 * reads refetch after a write. Kept in one place so keys never drift.
 */
export const patientKeys = {
  all: ['patients'] as const,
  list: (params: SearchPatientsParams) => ['patients', 'list', params] as const,
  detail: (patientId: string) => ['patients', 'detail', patientId] as const,
  corrections: (patientId: string) => ['patients', 'detail', patientId, 'corrections'] as const,
  tags: () => ['patients', 'tags'] as const,
};

/** Read — a paginated page of the clinic patient directory. */
export function usePatientsQuery(params: SearchPatientsParams) {
  return useQuery<PaginatedResponse<PatientListItemResponse>>({
    queryKey: patientKeys.list(params),
    queryFn: () => patientsApi.search(params),
  });
}

/** Read — the clinic's active tag catalog, for the directory tag filter. Cached longer; it rarely changes. */
export function useClinicTagsQuery() {
  return useQuery<ClinicTagResponse[]>({
    queryKey: patientKeys.tags(),
    queryFn: () => patientsApi.listTags(),
    staleTime: 5 * 60 * 1000,
  });
}

/** Read — a single patient's full chart detail (demographics + alerts + tags). */
export function usePatientQuery(patientId: string | undefined) {
  return useQuery<PatientDetailResponse>({
    queryKey: patientKeys.detail(patientId ?? ''),
    queryFn: () => patientsApi.getById(patientId as string),
    enabled: Boolean(patientId),
  });
}

/** Read — patient-submitted demographic corrections for staff review. */
export function usePatientDetailCorrectionsQuery(patientId: string | undefined) {
  return useQuery<PatientDetailCorrectionResponse[]>({
    queryKey: patientKeys.corrections(patientId ?? ''),
    queryFn: () => patientsApi.listDetailCorrections(patientId as string),
    enabled: Boolean(patientId),
  });
}
